import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  FiPlus, FiEdit2, FiTrash2, FiEye, FiEyeOff,
  FiSearch, FiPackage, FiFilter,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import productService from '../../services/product.service';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const FarmerProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deleting, setDeleting] = useState(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const { data } = await productService.getMyProducts(params);
      setProducts(data.data);
      setPagination(data.pagination);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleDelete = async (product) => {
    if (!window.confirm(`Are you sure you want to delete "${product.name}"?`)) return;
    setDeleting(product._id);
    try {
      await productService.delete(product._id);
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    } finally {
      setDeleting(null);
    }
  };

  const handleToggleStatus = async (product) => {
    const newStatus = product.status === 'active' ? 'draft' : 'active';
    try {
      await productService.update(product._id, { status: newStatus });
      toast.success(`Product ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchProducts();
  };

  return (
    <div className="section-padding">
      <div className="container">
        {/* Header */}
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
          <div>
            <h2 className="mb-1">My Products</h2>
            <p className="text-muted mb-0">{pagination.total || 0} products total</p>
          </div>
          <Link to="/merchant/products/new" className="btn btn-fd-primary">
            <FiPlus className="me-1" /> Add Product
          </Link>
        </div>

        {/* Filters */}
        <div className="dashboard-card mb-4">
          <div className="row g-3 align-items-end">
            <div className="col-md-6">
              <form onSubmit={handleSearch} className="input-group">
                <span className="input-group-text bg-white"><FiSearch /></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search products..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button className="btn btn-outline-primary" type="submit">Search</button>
              </form>
            </div>
            <div className="col-md-3">
              <div className="d-flex align-items-center gap-2">
                <FiFilter className="text-muted" />
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Products Table */}
        {loading ? (
          <LoadingSpinner />
        ) : products.length > 0 ? (
          <>
            <div className="dashboard-card p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '50px' }}></th>
                      <th>Product</th>
                      <th>Category</th>
                      <th>Price</th>
                      <th>Stock</th>
                      <th>Status</th>
                      <th style={{ width: '150px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => {
                      const primaryImage = product.images?.find((img) => img.isPrimary) || product.images?.[0];
                      const stockQty = product.stock?.quantity;
                      const lowStock = product.stock?.trackInventory &&
                        stockQty > 0 && stockQty <= (product.stock?.lowStockThreshold || 10);

                      return (
                        <tr key={product._id}>
                          <td>
                            {primaryImage?.url ? (
                              <img
                                src={primaryImage.url}
                                alt=""
                                className="rounded"
                                style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                              />
                            ) : (
                              <div
                                className="rounded bg-light d-flex align-items-center justify-content-center"
                                style={{ width: '40px', height: '40px' }}
                              >
                                <FiPackage size={16} className="text-muted" />
                              </div>
                            )}
                          </td>
                          <td>
                            <strong className="d-block">{product.name}</strong>
                            {product.sku && <small className="text-muted">SKU: {product.sku}</small>}
                          </td>
                          <td><span className="badge bg-light text-dark">{product.category}</span></td>
                          <td>
                            <strong>${product.price.toFixed(2)}</strong>
                            {product.compareAtPrice && product.compareAtPrice > product.price && (
                              <small className="text-muted text-decoration-line-through d-block">
                                ${product.compareAtPrice.toFixed(2)}
                              </small>
                            )}
                          </td>
                          <td>
                            {product.stock?.trackInventory ? (
                              <span className={`badge ${stockQty === 0 ? 'bg-danger' : lowStock ? 'bg-warning' : 'bg-success'}`}>
                                {stockQty === 0 ? 'Out of Stock' : `${stockQty} units`}
                              </span>
                            ) : (
                              <span className="text-muted small">Untracked</span>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${product.status === 'active' ? 'bg-success' : product.status === 'draft' ? 'bg-secondary' : 'bg-warning'}`}>
                              {product.status}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <Link
                                to={`/merchant/products/${product._id}/edit`}
                                className="btn btn-sm btn-outline-primary"
                                title="Edit"
                              >
                                <FiEdit2 size={14} />
                              </Link>
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                title={product.status === 'active' ? 'Deactivate' : 'Activate'}
                                onClick={() => handleToggleStatus(product)}
                              >
                                {product.status === 'active' ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                title="Delete"
                                onClick={() => handleDelete(product)}
                                disabled={deleting === product._id}
                              >
                                <FiTrash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <nav className="d-flex justify-content-center mt-4">
                <ul className="pagination">
                  <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setPage(page - 1)}>Previous</button>
                  </li>
                  {Array.from({ length: pagination.pages }, (_, i) => (
                    <li key={i + 1} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => setPage(i + 1)}>{i + 1}</button>
                    </li>
                  ))}
                  <li className={`page-item ${page >= pagination.pages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setPage(page + 1)}>Next</button>
                  </li>
                </ul>
              </nav>
            )}
          </>
        ) : (
          <div className="text-center py-5">
            <FiPackage size={64} className="text-muted mb-3" />
            <h4>No products yet</h4>
            <p className="text-muted mb-4">Start by adding your first product.</p>
            <Link to="/merchant/products/new" className="btn btn-fd-primary">
              <FiPlus className="me-1" /> Add Product
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default FarmerProductsPage;
